import React, { useEffect, useState } from "react";
import { api, inrPlain } from "@/lib/api";
import { useGroup } from "@/context/GroupContext";
import { CardSkeleton } from "@/components/Skeletons";
import {
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, LineChart, Line, CartesianGrid, Legend,
} from "recharts";
import { TrendingUp, Users, Coins, Receipt } from "lucide-react";

export default function Reports() {
  const { groupId } = useGroup();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!groupId) return;
    api.get("/reports/overview", { params: { group_id: groupId } }).then((r) => setData(r.data));
  }, [groupId]);

  if (!data) return <CardSkeleton />;

  const topPayer = data.member_wise[0];
  const topOwer = [...data.net_positions].sort((a, b) => a.diff - b.diff)[0];

  return (
    <div className="space-y-5">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Analytics</div>
        <h1 className="heading text-3xl font-bold tracking-tight text-slate-900 mt-1">Reports</h1>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Stat icon={Receipt} label="Expenses" value={data.total_count} color="bg-emerald-50 text-emerald-600" />
        <Stat icon={Coins} label="Total spent" value={inrPlain(data.total_amount)} color="bg-slate-100 text-slate-900" />
        <Stat icon={TrendingUp} label="Settlements" value={data.settlement_count} color="bg-blue-50 text-blue-600" />
        <Stat icon={Users} label="Settled amt" value={inrPlain(data.settlement_total)} color="bg-purple-50 text-purple-600" />
      </div>

      {topPayer && (
        <div className="p-5 rounded-3xl bg-white border border-slate-200 flex items-center gap-4">
          <img src={topPayer.user.photo} alt="" className="w-12 h-12 rounded-full object-cover" />
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Top spender</div>
            <div className="font-semibold text-slate-900">{topPayer.user.name}</div>
            <div className="text-xs text-slate-500">paid {inrPlain(topPayer.paid)}</div>
          </div>
          {topOwer && topOwer.diff < 0 && (
            <div className="text-right">
              <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Top borrower</div>
              <div className="font-semibold text-rose-600">{topOwer.user.name}</div>
              <div className="text-xs text-slate-500">{inrPlain(Math.abs(topOwer.diff))}</div>
            </div>
          )}
        </div>
      )}

      {data.category_wise.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Card title="By category">
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={data.category_wise}
                  dataKey="amount"
                  nameKey="category"
                  innerRadius={55}
                  outerRadius={100}
                  stroke="none"
                >
                  {data.category_wise.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => inrPlain(v)} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
                <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </Card>

          {data.member_wise.length > 0 && (
            <Card title="Paid by member">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.member_wise.map((m) => ({ name: m.user.name, paid: m.paid, share: m.share }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => inrPlain(v)} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="paid" fill="#10b981" radius={[8, 8, 0, 0]} />
                  <Bar dataKey="share" fill="#94a3b8" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}
        </div>
      )}

      {data.trend.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Card title="Expense trend">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={data.trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => inrPlain(v)} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
                <Line type="monotone" dataKey="amount" stroke="#0f172a" strokeWidth={2.5} dot={{ r: 3, fill: "#10b981" }} />
              </LineChart>
            </ResponsiveContainer>
          </Card>

          {data.monthly.length > 0 && (
            <Card title="Monthly spending">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => inrPlain(v)} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
                  <Bar dataKey="amount" fill="#8b5cf6" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="p-5 rounded-3xl bg-white border border-slate-200">
      <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">{title}</div>
      {children}
    </div>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="p-4 rounded-2xl bg-white border border-slate-200" data-testid={`stat-${label}`}>
      <div className={`w-9 h-9 rounded-xl grid place-items-center ${color}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold mt-3">{label}</div>
      <div className="amount font-bold text-slate-900 mt-1">{value}</div>
    </div>
  );
}
